// The visitor's own clock, and where the sun is in their sky.
//
// An agent deciding whether to reach out is really asking "is it a reasonable
// hour where they are?", and neither the timestamp column nor the country name
// answers that. 09:00 in Ahmedabad and 09:00 in Frankfurt are four and a half
// hours apart, and "India" alone does not say whether the person on the other
// end is starting their day or asleep.
//
// Two independent inputs, and the better one is not the one we resolved:
//
//   * the visitor's OWN IANA zone, reported by their browser
//     (`Intl.DateTimeFormat().resolvedOptions().timeZone`). Exact, including
//     whatever DST rule applies today.
//   * their longitude, from the IP lookup. A fallback with real error in it —
//     solar longitude ignores political time zones entirely, so western China
//     is off by hours and half-hour offsets like India's are never exact.
//
// The zone is preferred wherever we have it, and the source is reported so the
// UI can decline to be precise about a number it guessed.

/** Where the sun is, in the terms a person would use. */
export type SkyPhase = 'day' | 'golden' | 'twilight' | 'night';

export interface LocalTime {
  /** Local wall-clock hour, 0–23. */
  hour: number;
  minute: number;
  /** "09:24" in the visitor's zone. */
  clock: string;
  /** Minutes east of UTC (IST = 330). */
  offsetMinutes: number;
  /** IANA zone when known — never invented from a longitude. */
  zone: string | null;
  /** Which input produced this: the browser's zone, or a guess from longitude. */
  source: 'zone' | 'longitude';
  /** Signed minutes the visitor is ahead of the viewing agent. */
  offsetFromViewer: number;
}

const MINUTES_PER_DAY = 24 * 60;

/**
 * Minutes east of UTC for `zone` at `at`, or null if the zone is unusable.
 *
 * Derived from the zone's own formatted offset rather than by arithmetic on
 * two Dates, so a DST boundary cannot land it an hour out.
 */
export function zoneOffsetMinutes(zone: string, at: Date): number | null {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      timeZoneName: 'longOffset',
    }).formatToParts(at);
    const name = parts.find((p) => p.type === 'timeZoneName')?.value ?? '';
    // "GMT+05:30", "GMT-08:00", or plain "GMT" at exactly zero.
    const m = /GMT([+-])(\d{1,2})(?::(\d{2}))?/.exec(name);
    if (!m) return name === 'GMT' ? 0 : null;
    const sign = m[1] === '-' ? -1 : 1;
    return sign * (Number(m[2]) * 60 + Number(m[3] ?? 0));
  } catch {
    // An unknown or malformed zone string — the widget reports whatever the
    // visitor's browser says, which we do not control.
    return null;
  }
}

/** Longitude → whole-hour offset. The crude fallback; see the module note. */
function longitudeOffsetMinutes(longitude: number): number {
  return Math.round(longitude / 15) * 60;
}

/**
 * Resolve the visitor's local time, or null when neither input is available.
 * `viewerZone` is the agent's own zone, used only for the relative offset.
 */
export function resolveLocalTime(
  input: { timezone?: string | null; longitude?: number | null },
  viewerZone: string,
  now: Date = new Date(),
): LocalTime | null {
  let offsetMinutes: number | null = null;
  let zone: string | null = null;
  let source: LocalTime['source'] = 'zone';

  if (input.timezone) {
    const resolved = zoneOffsetMinutes(input.timezone, now);
    if (resolved !== null) {
      offsetMinutes = resolved;
      zone = input.timezone;
    }
  }
  if (offsetMinutes === null && typeof input.longitude === 'number' && Number.isFinite(input.longitude)) {
    offsetMinutes = longitudeOffsetMinutes(input.longitude);
    source = 'longitude';
  }
  if (offsetMinutes === null) return null;

  // Wall-clock minutes since local midnight. The modulo runs twice because a
  // negative offset can push the total below zero, and JS `%` keeps the sign.
  const utcMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();
  const localMinutes = ((utcMinutes + offsetMinutes) % MINUTES_PER_DAY + MINUTES_PER_DAY) % MINUTES_PER_DAY;
  const hour = Math.floor(localMinutes / 60);
  const minute = localMinutes % 60;

  const viewerOffset = zoneOffsetMinutes(viewerZone, now) ?? 0;

  return {
    hour,
    minute,
    clock: `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
    offsetMinutes,
    zone,
    source,
    offsetFromViewer: offsetMinutes - viewerOffset,
  };
}

/**
 * Solar elevation in degrees at a place and instant — negative below the
 * horizon. NOAA's low-precision algorithm, which is good to about a tenth of a
 * degree: far finer than "is it dark there", which is all this drives.
 */
export function solarElevation(
  latitude: number,
  longitude: number,
  at: Date,
): number {
  const rad = Math.PI / 180;
  const start = Date.UTC(at.getUTCFullYear(), 0, 1);
  const dayOfYear = Math.floor((at.getTime() - start) / 86_400_000) + 1;
  const utcHours = at.getUTCHours() + at.getUTCMinutes() / 60 + at.getUTCSeconds() / 3600;

  // Fractional year, radians.
  const g = ((2 * Math.PI) / 365) * (dayOfYear - 1 + (utcHours - 12) / 24);
  // Equation of time (minutes) and solar declination (radians).
  const eqTime =
    229.18 *
    (0.000075 +
      0.001868 * Math.cos(g) -
      0.032077 * Math.sin(g) -
      0.014615 * Math.cos(2 * g) -
      0.040849 * Math.sin(2 * g));
  const decl =
    0.006918 -
    0.399912 * Math.cos(g) +
    0.070257 * Math.sin(g) -
    0.006758 * Math.cos(2 * g) +
    0.000907 * Math.sin(2 * g) -
    0.002697 * Math.cos(3 * g) +
    0.00148 * Math.sin(3 * g);

  // True solar time, minutes. Longitude is used directly against UTC, so the
  // political time zone never enters this calculation — the sun does not know
  // about it either.
  const trueSolarTime = (utcHours * 60 + eqTime + 4 * longitude + 1440) % 1440;
  const hourAngle = trueSolarTime / 4 - 180;

  const cosZenith =
    Math.sin(latitude * rad) * Math.sin(decl) +
    Math.cos(latitude * rad) * Math.cos(decl) * Math.cos(hourAngle * rad);
  // Guard the acos domain: rounding can push this a hair outside [-1, 1].
  const zenith = Math.acos(Math.min(1, Math.max(-1, cosZenith)));
  return 90 - zenith / rad;
}

/**
 * Name the phase from a solar elevation. The boundaries are the conventional
 * ones: civil twilight ends at -6°, and the golden hour is the sun's first 6°
 * above the horizon.
 */
export function phaseFromElevation(elevation: number): SkyPhase {
  if (elevation > 6) return 'day';
  if (elevation > 0) return 'golden';
  if (elevation > -6) return 'twilight';
  return 'night';
}

/** Fallback phase when there are no coordinates — the hour alone. */
export function phaseFromHour(hour: number): SkyPhase {
  if (hour >= 8 && hour < 17) return 'day';
  if (hour >= 6 && hour < 8) return 'golden';
  if (hour >= 17 && hour < 19) return 'golden';
  if (hour >= 19 && hour < 21) return 'twilight';
  return 'night';
}

export const PHASE_LABEL: Record<SkyPhase, string> = {
  day: 'Daytime',
  golden: 'Golden hour',
  twilight: 'Twilight',
  night: 'Night',
};

/** "4h 30m ahead" / "2h behind" / "same time as you". */
export function offsetLabel(minutes: number): string {
  if (minutes === 0) return 'same time as you';
  const abs = Math.abs(minutes);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const parts = [h > 0 ? `${h}h` : '', m > 0 ? `${m}m` : ''].filter(Boolean).join(' ');
  return `${parts} ${minutes > 0 ? 'ahead' : 'behind'}`;
}
